import type { ReactNode } from "react";
import { DialogContent } from "@/components/ui/dialog";
import { cn } from "@/lib/utils";

export const payoutPageShellClass = "min-h-screen bg-background";
export const payoutContainerClass = "mx-auto w-full max-w-5xl space-y-8 px-4 py-7 sm:px-6 sm:py-9 lg:space-y-10 lg:py-10";
export const payoutWideContainerClass = "mx-auto w-full max-w-7xl space-y-8 px-4 py-7 sm:px-6 sm:py-9 lg:space-y-10 lg:py-10";
export const payoutPremiumCardClass = "overflow-hidden rounded-[1.1rem] border border-border/40 bg-transparent shadow-none";
export const payoutTabsListClass = "grid w-full rounded-full border border-border/40 bg-muted/30 p-1 shadow-none backdrop-blur-xl";
export const payoutOutlineBadgeClass = "w-fit rounded-full border border-success/25 dark:border-success/[0.18] bg-success/10 dark:bg-success/[0.065] px-3 py-1 text-xs font-semibold text-success";
export const payoutFieldControlClass = "h-10 rounded-full border-border/40 bg-transparent px-4 shadow-none transition-colors placeholder:text-muted-foreground/50 hover:border-primary/22 focus-visible:ring-0 focus-visible:ring-offset-0";
export const payoutSelectTriggerClass = "h-10 rounded-full border-border/40 bg-transparent px-4 shadow-none transition-colors hover:border-primary/22 focus:ring-0 focus:ring-offset-0 focus-visible:ring-0 focus-visible:ring-offset-0";
export const payoutPrimaryActionClass = "h-10 rounded-full border border-success/30 dark:border-success/[0.24] bg-success/10 dark:bg-success/[0.08] px-4 text-sm font-semibold text-success shadow-none transition-colors hover:border-success/50 dark:hover:border-success/40 hover:bg-success/15 dark:hover:bg-success/[0.13] hover:text-success focus-visible:ring-0 focus-visible:ring-offset-0 disabled:border-border/30 disabled:bg-transparent disabled:text-muted-foreground/45 disabled:shadow-none";
export const payoutSecondaryActionClass = "h-10 rounded-full border border-border/40 bg-transparent px-4 text-sm font-semibold text-foreground/76 shadow-none transition-colors hover:border-primary/22 hover:bg-muted/40 hover:text-foreground focus-visible:ring-0 focus-visible:ring-offset-0";

export function GetPaidCard({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  return <section className={cn("border-t border-border/40 pt-6", className)}>{children}</section>;
}

export function GetPaidSectionTitle({
  title,
  description,
  action,
}: {
  title: ReactNode;
  description?: ReactNode;
  action?: ReactNode;
}) {
  return (
    <div className="mb-5 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
      <div className="min-w-0 space-y-1.5">
        <h2 className="text-lg font-semibold tracking-[-0.025em] text-foreground">{title}</h2>
        {description && <p className="max-w-2xl text-sm leading-6 text-muted-foreground/74">{description}</p>}
      </div>
      {action && <div className="shrink-0">{action}</div>}
    </div>
  );
}

export function GetPaidMetricGrid({ children, className }: { children: ReactNode; className?: string }) {
  return <div className={cn("grid rounded-lg border border-border/40 sm:grid-cols-2 lg:grid-cols-4", className)}>{children}</div>;
}

export function GetPaidMetric({
  label,
  value,
  hint,
  tone = "neutral",
}: {
  label: ReactNode;
  value: ReactNode;
  hint?: ReactNode;
  tone?: "neutral" | "emerald" | "amber" | "blue";
}) {
  const toneClass = {
    neutral: "text-foreground",
    emerald: "text-success",
    amber: "text-brand",
    blue: "text-info",
  }[tone];

  return (
    <div className="border-border/30 py-4 sm:px-4 sm:[&:nth-child(even)]:border-l lg:border-l lg:first:border-l-0">
      <p className="text-[0.66rem] font-semibold uppercase tracking-[0.18em] text-muted-foreground/58">{label}</p>
      <p className={cn("mt-2 text-2xl font-semibold leading-none tracking-tight", toneClass)}>{value}</p>
      {hint && <p className="mt-2 max-w-[18rem] text-xs leading-5 text-muted-foreground/66">{hint}</p>}
    </div>
  );
}

export function GetPaidField({
  label,
  htmlFor,
  hint,
  children,
  className,
}: {
  label: ReactNode;
  htmlFor?: string;
  hint?: ReactNode;
  children: ReactNode;
  className?: string;
}) {
  return (
    <div className={cn("space-y-2", className)}>
      <label htmlFor={htmlFor} className="text-[0.68rem] font-semibold uppercase tracking-[0.18em] text-muted-foreground/62">
        {label}
      </label>
      {children}
      {hint && <p className="text-xs leading-5 text-muted-foreground/70">{hint}</p>}
    </div>
  );
}

export function GetPaidActionPanel({ children, className }: { children: ReactNode; className?: string }) {
  return <div className={cn("grid gap-2.5 sm:grid-cols-2 lg:grid-cols-3", className)}>{children}</div>;
}

export function GetPaidNotice({
  tone = "amber",
  children,
  className,
}: {
  tone?: "amber" | "emerald" | "blue" | "neutral";
  children: ReactNode;
  className?: string;
}) {
  const toneClass = {
    amber: "text-brand before:bg-primary dark:before:bg-brand/70",
    emerald: "text-success before:bg-success dark:before:bg-success/70",
    blue: "text-info before:bg-info dark:before:bg-info/70",
    neutral: "text-foreground/80 before:bg-foreground/35",
  }[tone];

  return (
    <div className={cn("relative border-t border-border/40 py-3 pl-5 text-sm before:absolute before:left-0 before:top-4 before:h-1.5 before:w-1.5 before:rounded-full", toneClass, className)}>
      {children}
    </div>
  );
}

export function GetPaidDialogContent({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <DialogContent className={cn("max-w-lg rounded-[1.15rem] border-border/40 bg-background dark:bg-background/95 p-5 shadow-none backdrop-blur-2xl sm:p-6", className)}>
      {children}
    </DialogContent>
  );
}

export function GetPaidEmptyState({ children }: { children: ReactNode }) {
  return (
    <div className="rounded-lg border border-dashed border-border/40 px-2 py-8 text-center text-sm text-muted-foreground/72">
      {children}
    </div>
  );
}
